import { useState } from 'react';
import type { BankAccountDto } from '@signalvote/shared';
import { useBankAccounts } from '@/hooks/queries/useBankIngestion';
import { EditBankProfileForm } from './EditBankProfileForm';

/**
 * PPR-017 — Settings host page for the Edit-bank-profile flow. Lists every
 * <see cref="BankAccountDto"/> in the campaign and lets the treasurer pick
 * one to edit "how we read your statements" against. Pre-shipped profiles
 * surface as read-only via the embedded <see cref="EditBankProfileForm"/>.
 *
 * <p>Reachable at <code>/admin/finance/settings/bank-accounts</code>.</p>
 */
export function BankAccountSettingsPage() {
  const { data: accounts, isLoading, error } = useBankAccounts();
  const [selectedAccount, setSelectedAccount] = useState<BankAccountDto | null>(null);

  if (isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading bank accounts…</div>;
  }
  if (error) {
    return (
      <div role="alert" className="m-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Couldn&apos;t load bank accounts.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
      <header>
        <h1 className="text-2xl font-bold">Bank Accounts</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Edit how we read each bank&apos;s statements. Changes apply to future uploads —
          existing imports stay as they were.
        </p>
      </header>

      {(!accounts || accounts.length === 0) ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm">
          <p className="font-medium">No bank accounts configured yet.</p>
          <p className="mt-1 text-muted-foreground">
            Add one on the Setup tab before customizing how we read its statements.
          </p>
        </div>
      ) : (
        <section className="space-y-3">
          <ul className="divide-y rounded-md border bg-card">
            {accounts.map((account) => (
              <li key={account.id} className="flex items-center justify-between px-4 py-3">
                <div className="text-sm">
                  <p className="font-medium">{account.displayName}</p>
                  {!account.activeBankSourceProfileId && (
                    <p className="text-xs text-muted-foreground">
                      No bank profile yet — upload a statement to learn one.
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  data-testid={`edit-profile-${account.id}`}
                  onClick={() => setSelectedAccount(account)}
                  disabled={!account.activeBankSourceProfileId}
                  className="text-sm text-blue-700 underline hover:text-blue-900 disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed"
                >
                  Edit how we read your statements
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {selectedAccount?.activeBankSourceProfileId && (
        <section
          data-testid="edit-profile-section"
          className="rounded-md border bg-card p-4"
        >
          <header className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-semibold">
              Profile for {selectedAccount.displayName}
            </h2>
            <button
              type="button"
              onClick={() => setSelectedAccount(null)}
              className="text-xs text-muted-foreground underline hover:text-foreground"
            >
              Close
            </button>
          </header>
          <EditBankProfileForm
            profileId={selectedAccount.activeBankSourceProfileId}
            onSaved={() => {
              // Keep the form open — treasurer might want to tweak again.
            }}
          />
        </section>
      )}
    </div>
  );
}
